import { queryOptions } from '@tanstack/react-query';

import { ONE_MINUTE } from '@/constants/time';
import type { paths } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';
import { Sentry } from '@/util/sentry';

/**
 * App configuration with parallel OTA/store update policies.
 *
 * Default behavior: background checks enabled, silent downloads, no forced updates.
 * Server can override any policy to control update behavior remotely.
 *
 * To disable custom logic: use { otaUpdate: { mode: 'auto' }, storeUpdate: { mode: 'auto' } }.
 * This falls back to Expo's default cold-launch update behavior.
 */
export type AppConfig = NonNullable<
  paths['/v2/app-config']['get']['responses'][200]['content']['application/json']['data']
>;

/**
 * Default configuration following Expo best practices.
 */
const DEFAULT_APP_CONFIG: AppConfig = {
  otaUpdate: {
    mode: 'auto',
    required: false,
  },
  storeUpdate: {
    mode: 'auto',
    required: false,
  },
  featureFlags: {
    autoReferences: {
      enabled: false,
      allowedExpertIds: [],
    },
  },
};

/**
 * Fetches app configuration from server endpoint.
 * Falls back to defaults if endpoint fails.
 *
 * Behavior:
 * - On success: returns server config or falls back to defaults.
 * - On failure: tracks error in Sentry and returns default config.
 */
async function getAppConfigV2(): Promise<AppConfig> {
  try {
    const { data } = await fetchClient.GET('/v2/app-config');

    const config = data?.data ?? DEFAULT_APP_CONFIG;

    return config;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        scope: 'app-config',
        operation: 'fetch',
      },
      extra: {
        currentVersion: process.env.EXPO_PUBLIC_APP_VERSION,
        endpoint: '/v2/app-config',
        fallbackConfig: DEFAULT_APP_CONFIG,
      },
    });

    return DEFAULT_APP_CONFIG;
  }
}

/**
 * React Query options for fetching app configuration.
 *
 * Caches configuration for 5 minutes and refetches on foreground focus
 * when stale for responsive config updates without battery drain.
 */
export const getAppConfigV2QueryOptions = () =>
  queryOptions({
    queryKey: ['app-config-v2'],
    queryFn: getAppConfigV2,
    staleTime: 5 * ONE_MINUTE,
    retry: 2,
  });
